import { useEffect } from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  getEmptyGuestAddress,
  GuestAddress,
} from '../../interfaces/Address/GuestAddress';
import { PaymentForm } from '../../interfaces/Checkout/PaymentForm';
import { useValidateAddress } from './utils';
import { cities, statesByCity } from '../../constants/Address';
import { useLocalizedText } from '../../hooks/useLocalizedText';

type CheckoutGuestAddressProps = {
  form: PaymentForm;
  onChange: (value: GuestAddress) => void;
};

export const CheckoutGuestAddress = ({
  form,
  onChange,
}: CheckoutGuestAddressProps) => {
  const t = useLocalizedText();

  const guestAddress = form.guestAddress;

  const errors = useValidateAddress(form);

  useEffect(() => {
    if (!guestAddress) {
      onChange(getEmptyGuestAddress());
    }
  }, [form]);

  if (!guestAddress) {
    return null;
  }

  return (
    <Paper
      elevation={3}
      sx={{
        px: 2.5,
        py: 2,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            width: '100%',
          }}
        >
          <Typography>{t('address.city')}</Typography>

          <Select
            size='small'
            fullWidth
            required
            value={guestAddress.city}
            onChange={(e) => {
              const city = e.target.value;

              onChange({
                ...guestAddress,
                city,
                state: statesByCity[city][0],
              });
            }}
          >
            {cities.map((city) => (
              <MenuItem key={city} value={city}>
                {city}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            width: '100%',
          }}
        >
          <Typography>{t('address.province')}</Typography>

          <Select
            size='small'
            fullWidth
            required
            value={guestAddress.state}
            onChange={(e) => {
              onChange({
                ...guestAddress,
                state: e.target.value,
              });
            }}
          >
            {statesByCity[guestAddress.city].map((state) => (
              <MenuItem key={state} value={state}>
                {state}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          width: '100%',
        }}
      >
        <Typography>{t('address.address')}</Typography>

        <TextField
          size='small'
          fullWidth
          multiline
          minRows={3}
          value={guestAddress.address}
          onChange={(e) =>
            onChange({ ...guestAddress, address: e.target.value })
          }
          error={!!errors.address}
          helperText={errors.address}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          width: '100%',
        }}
      >
        <Typography>{t('address.zip_code')}</Typography>

        <TextField
          size='small'
          fullWidth
          value={guestAddress.zip ?? ''}
          onChange={(e) => onChange({ ...guestAddress, zip: e.target.value })}
        />
      </Box>

      <FormControl>
        <FormLabel>{t('address.address_type')}</FormLabel>
        <RadioGroup
          value={guestAddress.addressType.toString()}
          onChange={(e) =>
            onChange({ ...guestAddress, addressType: Number(e.target.value) })
          }
        >
          <FormControlLabel
            value='0'
            control={<Radio />}
            label={t('address.individual')}
          />
          <FormControlLabel
            value='1'
            control={<Radio />}
            label={t('address.company')}
          />
        </RadioGroup>
      </FormControl>

      {guestAddress.addressType === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              width: '100%',
            }}
          >
            <Typography>{t('address.tax_office')}</Typography>

            <TextField
              size='small'
              fullWidth
              value={guestAddress.taxOffice ?? ''}
              onChange={(e) =>
                onChange({ ...guestAddress, taxOffice: e.target.value })
              }
              error={!!errors.taxOffice}
              helperText={errors.taxOffice}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              width: '100%',
            }}
          >
            <Typography>{t('address.tax_number')}</Typography>

            <TextField
              size='small'
              fullWidth
              value={guestAddress.taxNumber ?? ''}
              onChange={(e) =>
                onChange({ ...guestAddress, taxNumber: e.target.value })
              }
              error={!!errors.taxNumber}
              helperText={errors.taxNumber}
            />
          </Box>
        </Box>
      )}
    </Paper>
  );
};
